import React, { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ChevronLeft, ShieldAlert, LayoutDashboard, Store, Package, GraduationCap, CheckCircle2, XCircle,
  Settings, Clock, BadgeCheck, Eye, X, MessageSquareWarning, Layers, PlayCircle, Info, Receipt, Percent, Loader2,
  HardDrive, Trash2, RefreshCw, Wallet, ArrowDownToLine, BarChart3, Download, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  CATEGORIAS_DIGITALES, COURSE_CATEGORIES, MKT_GOLD, MKT_TINT,
} from "../lib/marketplace";
import { apiAdminResumen, apiAdminPendientes, apiAdminListado, apiAdminModerar, apiAdminAlmacenamiento, apiAdminMantenimiento, bytesLegible, apiAdminRetiros, apiAprobarRetiro, apiRechazarRetiro, apiPagarRetiro, apiAdminEstadisticas, apiAdminEstadisticasCsv, apiAdminConfig, apiAdminReembolsos, apiAprobarReembolso, apiRechazarReembolso } from "../lib/marketplaceApi";
import { PeriodoSelector, StatKpi, SerieBars, Ranking, rangoDesde, soles as solesFmt, PRESETS } from "./marketplace/statsui";
import { humanizeAxiosError } from "../lib/api";
import { EstadoBadge, COURSE_NOTICE } from "./marketplace/mktui";

const SECCIONES = [
  { key: "resumen", label: "Resumen", Icon: LayoutDashboard },
  { key: "estadisticas", label: "Estadísticas", Icon: BarChart3 },
  { key: "tiendas", label: "Tiendas por revisar", Icon: Store },
  { key: "productos", label: "Productos por revisar", Icon: Package },
  { key: "cursos", label: "Cursos por revisar", Icon: GraduationCap },
  { key: "publicados", label: "Publicados", Icon: CheckCircle2 },
  { key: "rechazados", label: "Rechazados", Icon: XCircle },
  { key: "almacenamiento", label: "Almacenamiento", Icon: HardDrive },
  { key: "retiros", label: "Retiros", Icon: Wallet },
  { key: "reembolsos", label: "Reembolsos", Icon: RotateCcw },
  { key: "config", label: "Configuración", Icon: Settings },
];

const dcat = (id) => CATEGORIAS_DIGITALES.find((c) => c.id === id)?.label || id;
const ccat = (id) => COURSE_CATEGORIES.find((c) => c.id === id)?.label || id;
const nMod = (c) => (typeof c.modulos === "number" ? c.modulos : c.modulos?.length || 0);
const nLec = (c) => (typeof c.lecciones === "number" ? c.lecciones : (c.modulos || []).reduce((n, m) => n + (m.lecciones?.length || 0), 0));

function KPI({ Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5" data-testid={`mkta-kpi-${label}`}>
      <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}1a`, color }}><Icon size={20} /></span>
      <p className="mt-3 text-2xl font-extrabold text-slate-900 leading-none">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{label}</p>
    </div>
  );
}

function FilaPendiente({ nombre, sub, meta, estado, onVer, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-50 last:border-0" data-testid={testid}>
      <div className="min-w-0">
        <p className="font-semibold text-slate-800 truncate">{nombre}</p>
        <p className="text-xs text-slate-500 truncate">{sub}</p>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {meta && <span className="text-xs text-slate-400 hidden sm:inline-flex items-center gap-1"><Clock size={12} /> {meta}</span>}
        <EstadoBadge estado={estado} tipo={testid?.includes("tienda") ? "tienda" : "producto"} />
        <button onClick={onVer} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-bold" style={{ backgroundColor: MKT_GOLD }}>
          <Eye size={13} /> Ver solicitud
        </button>
      </div>
    </div>
  );
}

export default function MarketplaceAdmin() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [seccion, setSeccion] = useState("resumen");
  const [review, setReview] = useState(null); // { tipo, item }
  const [highlightId, setHighlightId] = useState(null);
  const handledRef = useRef(null);
  const isAdmin = !user || user.role === "admin";

  const [cargando, setCargando] = useState(true);
  const [resumen, setResumen] = useState({});
  const [pend, setPend] = useState({ tiendas: [], productos: [], cursos: [] });
  const [publicados, setPublicados] = useState([]);
  const [rechazados, setRechazados] = useState([]);
  const [alma, setAlma] = useState(undefined);
  const [manteniendo, setManteniendo] = useState(false);
  const [retiros, setRetiros] = useState(undefined);
  const [accionR, setAccionR] = useState(null);
  const [reembolsos, setReembolsos] = useState(undefined);
  const [accionRe, setAccionRe] = useState(null);
  const [preset, setPreset] = useState("30d");
  const [stats, setStats] = useState(undefined);
  const [bajandoCsv, setBajandoCsv] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    if (seccion === "config" && isAdmin && !config) apiAdminConfig().then(setConfig).catch(() => {});
    // eslint-disable-next-line
  }, [seccion]);

  useEffect(() => {
    if (seccion !== "estadisticas" || !isAdmin) return;
    const dias = PRESETS.find((p) => p.key === preset).dias;
    const { desde, hasta } = rangoDesde(dias);
    setStats(undefined);
    apiAdminEstadisticas(desde, hasta).then(setStats).catch(() => setStats(null));
    // eslint-disable-next-line
  }, [seccion, preset]);

  const exportarStats = async () => {
    setBajandoCsv(true);
    try {
      const dias = PRESETS.find((p) => p.key === preset).dias;
      const { desde, hasta } = rangoDesde(dias);
      await apiAdminEstadisticasCsv(desde, hasta);
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setBajandoCsv(false); }
  };

  const cargarRetiros = async () => {
    setRetiros(undefined);
    try { setRetiros(await apiAdminRetiros()); } catch { setRetiros(null); }
  };
  useEffect(() => {
    if (seccion === "retiros" && isAdmin) cargarRetiros();
    // eslint-disable-next-line
  }, [seccion]);

  const accionRetiro = async (fn, wid, ...args) => {
    setAccionR(wid);
    try { await fn(wid, ...args); toast.success("Retiro actualizado."); await cargarRetiros(); }
    catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setAccionR(null); }
  };

  const cargarReembolsos = async () => {
    setReembolsos(undefined);
    try { setReembolsos(await apiAdminReembolsos()); } catch { setReembolsos(null); }
  };
  useEffect(() => {
    if (seccion === "reembolsos" && isAdmin) cargarReembolsos();
    // eslint-disable-next-line
  }, [seccion]);

  const accionReembolso = async (fn, rid, comentario) => {
    setAccionRe(rid);
    try { await fn(rid, comentario); toast.success("Solicitud actualizada."); await cargarReembolsos(); await recargar(); }
    catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setAccionRe(null); }
  };

  const cargarAlma = async () => {
    setAlma(undefined);
    try { setAlma(await apiAdminAlmacenamiento()); } catch { setAlma(null); }
  };
  useEffect(() => {
    if (seccion === "almacenamiento" && isAdmin) cargarAlma();
    // eslint-disable-next-line
  }, [seccion]);

  const ejecutarMantenimiento = async () => {
    setManteniendo(true);
    try {
      const r = await apiAdminMantenimiento();
      toast.success(`Mantenimiento listo: ${r.purgados} purgados, ${r.huerfanos_a_papelera} huérfanos a papelera.`);
      await cargarAlma();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setManteniendo(false); }
  };

  const recargar = async () => {
    try {
      const [res, p, pub, rej] = await Promise.all([
        apiAdminResumen(),
        apiAdminPendientes(),
        apiAdminListado("activa,publicado"),
        apiAdminListado("rechazada,rechazado"),
      ]);
      setResumen(res);
      setPend(p);
      setPublicados(pub);
      setRechazados(rej);
    } catch (e) {
      // 403 si no es admin
    } finally {
      setCargando(false);
    }
  };
  useEffect(() => {
    if (isAdmin) recargar();
    else setCargando(false);
    // eslint-disable-next-line
  }, []);

  // ---- Deep-link: cambia de sección según ?seccion= ----
  useEffect(() => {
    const s = searchParams.get("seccion");
    if (s && SECCIONES.some((x) => x.key === s)) setSeccion(s);
    // eslint-disable-next-line
  }, [searchParams]);

  const resaltar = (testid) => {
    setHighlightId(testid);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${testid}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    setTimeout(() => setHighlightId(null), 3400);
  };

  // ---- Deep-link a tienda/producto/curso → abre el modal del registro exacto ----
  useEffect(() => {
    const openId = searchParams.get("open");
    const s = searchParams.get("seccion");
    if (!openId || handledRef.current === openId) return;
    if (!["tiendas", "productos", "cursos"].includes(s)) return;
    const pools = [
      ...(pend.tiendas || []).map((i) => ({ tipo: "tienda", item: i })),
      ...(pend.productos || []).map((i) => ({ tipo: "producto", item: i })),
      ...(pend.cursos || []).map((i) => ({ tipo: "curso", item: i })),
      ...publicados,
      ...rechazados,
    ];
    const found = pools.find(({ item }) => item.id === openId);
    if (found) { handledRef.current = openId; setReview(found); }
    // eslint-disable-next-line
  }, [pend, publicados, rechazados, searchParams]);

  // ---- Deep-link a retiros → scroll + resaltado de la tarjeta ----
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || handledRef.current === openId || searchParams.get("seccion") !== "retiros" || !retiros) return;
    if ((retiros.items || []).some((r) => r.id === openId)) { handledRef.current = openId; resaltar(`mkta-retiro-${openId}`); }
    // eslint-disable-next-line
  }, [retiros, searchParams]);

  // ---- Deep-link a reembolsos → scroll + resaltado de la tarjeta ----
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || handledRef.current === openId || searchParams.get("seccion") !== "reembolsos" || !reembolsos) return;
    if ((reembolsos.items || []).some((r) => r.id === openId)) { handledRef.current = openId; resaltar(`mkta-reembolso-${openId}`); }
    // eslint-disable-next-line
  }, [reembolsos, searchParams]);

  if (user && user.role !== "admin") {
    return (
      <div className="max-w-lg mx-auto text-center py-20" data-testid="mkt-admin-denegado">
        <ShieldAlert size={40} className="mx-auto text-brand-red" />
        <h2 className="mt-4 text-xl font-bold text-slate-900">Acceso restringido</h2>
        <p className="mt-2 text-slate-500">La moderación del Marketplace es exclusiva para administradores.</p>
        <Link to="/" className="mt-4 inline-block text-brand-blue font-semibold hover:underline">Volver al inicio</Link>
      </div>
    );
  }

  const tiendasPend = pend.tiendas || [];
  const productosPend = pend.productos || [];
  const cursosPend = pend.cursos || [];
  const aprobadoHoy = resumen.aprobado_hoy || 0;
  const rechazadoHoy = resumen.rechazado_hoy || 0;
  const verificados = resumen.vendedores_verificados || 0;

  const decidir = async (tipo, item, decision, comentario, verificado) => {
    if (!comentario.trim()) return toast.error("Escribe un comentario administrativo para continuar.");
    try {
      await apiAdminModerar({ resource_type: tipo, resource_id: item.id, decision, comentario, vendedor_verificado: !!verificado });
      setReview(null);
      await recargar();
      toast.success(decision === "aprobado" ? "Contenido aprobado y publicado." : decision === "rechazado" ? "Contenido rechazado. El vendedor verá el comentario." : "Solicitud de corrección enviada al vendedor.");
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    }
  };

  if (cargando) {
    return <div className="flex items-center justify-center py-24 text-slate-500" data-testid="mkta-loading"><Loader2 className="animate-spin mr-2" /> Cargando panel…</div>;
  }

  return (
    <div className="px-5 md:px-8 py-8" data-testid="marketplace-admin">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <ChevronLeft size={16} /> Volver al Panel de Administración
      </Link>
      <header className="mt-4 mb-6 flex items-center gap-3">
        <span className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: MKT_TINT, color: MKT_GOLD }}><Store size={24} /></span>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: MKT_GOLD }}>Moderación · Marketplace</p>
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">Panel de Moderación de Marketplace</h1>
        </div>
      </header>

      {/* Tabs */}
      <nav className="flex gap-1 overflow-x-auto pb-2 mb-6 border-b border-slate-100" data-testid="mkta-tabs">
        {SECCIONES.map((s) => {
          const pend = s.key === "tiendas" ? tiendasPend.length : s.key === "productos" ? productosPend.length : s.key === "cursos" ? cursosPend.length : s.key === "reembolsos" ? (resumen.reembolsos_pendientes || 0) : 0;
          const active = seccion === s.key;
          return (
            <button key={s.key} onClick={() => setSeccion(s.key)} data-testid={`mkta-tab-${s.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${active ? "text-slate-900" : "text-slate-500 hover:text-slate-800 border-transparent"}`}
              style={active ? { borderColor: MKT_GOLD, color: "#92400E" } : undefined}>
              <s.Icon size={16} /> {s.label}
              {pend > 0 && <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: MKT_GOLD }}>{pend}</span>}
            </button>
          );
        })}
      </nav>

      {/* RESUMEN */}
      {seccion === "resumen" && (
        <div className="space-y-6" data-testid="mkta-resumen">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPI Icon={Store} label="Tiendas pendientes" value={tiendasPend.length} color="#D97706" />
            <KPI Icon={Package} label="Productos pendientes" value={productosPend.length} color="#2563EB" />
            <KPI Icon={GraduationCap} label="Cursos pendientes" value={cursosPend.length} color="#7E22CE" />
            <KPI Icon={BadgeCheck} label="Vendedores verificados" value={verificados} color="#16A34A" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPI Icon={CheckCircle2} label="Aprobado hoy" value={aprobadoHoy} color="#16A34A" />
            <KPI Icon={XCircle} label="Rechazado hoy" value={rechazadoHoy} color="#DC2626" />
            <KPI Icon={Receipt} label="Ventas (preliminar)" value="S/ 0.00" color="#0891B2" />
            <KPI Icon={Percent} label="Comisiones (preliminar)" value="S/ 0.00" color="#CA8A04" />
          </div>

          <section className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6" data-testid="mkta-pendientes-recientes">
            <h2 className="text-lg font-bold text-slate-900 mb-2">Pendientes recientes</h2>
            {tiendasPend.length + productosPend.length + cursosPend.length === 0 ? (
              <p className="text-sm text-slate-400 py-4">No hay contenido pendiente de revisión.</p>
            ) : (
              <div>
                {tiendasPend.map((t) => (
                  <FilaPendiente key={`t-${t.nombre}`} testid="mkta-pend-tienda" nombre={t.nombre} sub={`Tienda · ${t.vendedor}`} meta={t.enviadoEn} estado={t.estado} onVer={() => setReview({ tipo: "tienda", item: t })} />
                ))}
                {productosPend.map((p) => (
                  <FilaPendiente key={`p-${p.id}`} testid={`mkta-pend-prod-${p.id}`} nombre={p.titulo} sub={`Producto · ${dcat(p.categoria)}`} meta={p.enviadoEn} estado={p.estado} onVer={() => setReview({ tipo: "producto", item: p })} />
                ))}
                {cursosPend.map((c) => (
                  <FilaPendiente key={`c-${c.id}`} testid={`mkta-pend-curso-${c.id}`} nombre={c.titulo} sub={`Curso · ${ccat(c.categoria)}`} meta={c.updatedAt} estado={c.estado} onVer={() => setReview({ tipo: "curso", item: c })} />
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {/* TIENDAS */}
      {seccion === "tiendas" && (
        <ListaModeracion titulo="Tiendas por revisar" vacio="No hay tiendas pendientes de revisión." testid="mkta-lista-tiendas">
          {tiendasPend.map((t) => (
            <FilaPendiente key="tienda" testid="mkta-fila-tienda" nombre={t.nombre} sub={`Vendedor: ${t.vendedor} · ${(t.categorias || []).map(dcat).join(", ")}`} meta={t.enviadoEn} estado={t.estado} onVer={() => setReview({ tipo: "tienda", item: t })} />
          ))}
        </ListaModeracion>
      )}

      {/* PRODUCTOS */}
      {seccion === "productos" && (
        <ListaModeracion titulo="Productos por revisar" vacio="No hay productos pendientes de revisión." testid="mkta-lista-productos">
          {productosPend.map((p) => (
            <FilaPendiente key={p.id} testid={`mkta-fila-prod-${p.id}`} nombre={p.titulo} sub={`${dcat(p.categoria)} · S/ ${Number(p.precio || 0).toFixed(2)}`} meta={p.enviadoEn} estado={p.estado} onVer={() => setReview({ tipo: "producto", item: p })} />
          ))}
        </ListaModeracion>
      )}

      {/* CURSOS */}
      {seccion === "cursos" && (
        <ListaModeracion titulo="Cursos por revisar" vacio="No hay cursos pendientes de revisión." testid="mkta-lista-cursos">
          {cursosPend.map((c) => (
            <FilaPendiente key={c.id} testid={`mkta-fila-curso-${c.id}`} nombre={c.titulo} sub={`${ccat(c.categoria)} · ${c.instructor} · ${nMod(c)} módulos`} meta={c.updatedAt} estado={c.estado} onVer={() => setReview({ tipo: "curso", item: c })} />
          ))}
        </ListaModeracion>
      )}

      {/* PUBLICADOS */}
      {seccion === "publicados" && (
        <ListaModeracion titulo="Contenido publicado" vacio="Aún no hay contenido publicado." testid="mkta-lista-publicados">
          {publicados.map(({ tipo, item }) => (
            <FilaPendiente key={`${tipo}-${item.id || item.nombre}`} testid={`mkta-pub-${tipo}-${item.id || "tienda"}`} nombre={item.titulo || item.nombre} sub={tipo === "tienda" ? "Tienda" : tipo === "curso" ? "Curso independiente" : "Producto digital"} estado={item.estado} onVer={() => setReview({ tipo, item })} />
          ))}
        </ListaModeracion>
      )}

      {/* RECHAZADOS */}
      {seccion === "rechazados" && (
        <ListaModeracion titulo="Contenido rechazado" vacio="No hay contenido rechazado." testid="mkta-lista-rechazados">
          {rechazados.map(({ tipo, item }) => (
            <FilaPendiente key={`${tipo}-${item.id || item.nombre}`} testid={`mkta-rej-${tipo}-${item.id || "tienda"}`} nombre={item.titulo || item.nombre} sub={tipo === "tienda" ? "Tienda" : tipo === "curso" ? "Curso independiente" : "Producto digital"} estado={item.estado} onVer={() => setReview({ tipo, item })} />
          ))}
        </ListaModeracion>
      )}

      {/* ALMACENAMIENTO */}
      {seccion === "almacenamiento" && (
        <div className="space-y-6" data-testid="mkta-almacenamiento">
          {alma === undefined ? (
            <div className="flex items-center gap-2 text-slate-500 py-10"><Loader2 className="animate-spin" size={16} /> Cargando consumo…</div>
          ) : !alma ? (
            <p className="text-sm text-slate-400 py-6">No se pudo cargar el consumo de almacenamiento.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPI Icon={HardDrive} label="Consumo total" value={bytesLegible(alma.total_global)} color="#D97706" />
                <KPI Icon={Store} label="Tiendas" value={alma.tiendas.length} color="#2563EB" />
                <KPI Icon={Trash2} label="En papelera" value={alma.papelera} color="#DC2626" />
                <KPI Icon={Info} label="Huérfanos (>1 día)" value={alma.huerfanos_aprox} color="#CA8A04" />
              </div>

              {(alma.cerca_limite || []).length > 0 && (
                <section className="bg-white rounded-2xl border border-amber-200 shadow-soft p-6" data-testid="mkta-alma-cerca-limite">
                  <h2 className="text-lg font-bold text-slate-900 mb-2 inline-flex items-center gap-2"><Info size={18} className="text-amber-600" /> Tiendas cerca del límite (≥80%)</h2>
                  <ul className="divide-y divide-slate-50">
                    {alma.cerca_limite.map((t) => (
                      <li key={t.store_id} className="py-2 flex items-center justify-between text-sm" data-testid={`mkta-alma-cerca-${t.store_id}`}>
                        <span className="font-semibold text-slate-800 truncate">{t.tienda}</span>
                        <span className="font-bold text-amber-700">{t.porcentaje}% · {bytesLegible(t.usado)}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {(alma.archivos_pesados || []).length > 0 && (
                <section className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6" data-testid="mkta-alma-pesados">
                  <h2 className="text-lg font-bold text-slate-900 mb-2">Archivos más pesados</h2>
                  <ul className="divide-y divide-slate-50">
                    {alma.archivos_pesados.map((f) => (
                      <li key={f.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                        <span className="min-w-0 truncate text-slate-700">{f.filename} <span className="text-xs text-slate-400">· {f.tienda} · {f.scope}</span></span>
                        <span className="font-bold text-slate-900 flex-shrink-0">{bytesLegible(f.size)}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6" data-testid="mkta-alma-mantenimiento">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Mantenimiento de almacenamiento</h2>
                    <p className="text-sm text-slate-500 mt-0.5">Purga la papelera con más de {alma.trash_days} días y envía archivos huérfanos a la papelera.</p>
                  </div>
                  <button onClick={ejecutarMantenimiento} disabled={manteniendo} data-testid="mkta-alma-ejecutar" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-bold text-sm shadow-sm hover:brightness-110 disabled:opacity-60" style={{ backgroundColor: MKT_GOLD }}>
                    {manteniendo ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Ejecutar mantenimiento
                  </button>
                </div>
              </section>

              <section className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6" data-testid="mkta-alma-tiendas">
                <h2 className="text-lg font-bold text-slate-900 mb-3">Consumo por tienda</h2>
                {alma.tiendas.length === 0 ? (
                  <p className="text-sm text-slate-400 py-4">No hay tiendas registradas.</p>
                ) : (
                  <div className="space-y-3">
                    {alma.tiendas.map((t) => {
                      const pct = Math.min(100, t.porcentaje || 0);
                      const color = pct >= 90 ? "#DC2626" : pct >= 70 ? "#D97706" : MKT_GOLD;
                      return (
                        <div key={t.store_id} className="border-b border-slate-50 last:border-0 pb-3" data-testid={`mkta-alma-fila-${t.store_id}`}>
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-semibold text-slate-800 truncate">{t.tienda || "—"}</p>
                              <p className="text-xs text-slate-500 truncate">{t.vendedor} · {t.archivos} archivo(s) · {t.estado}</p>
                            </div>
                            <span className="text-sm font-bold flex-shrink-0" style={{ color }}>{bytesLegible(t.usado)} <span className="text-slate-400 font-semibold">/ {bytesLegible(t.cuota)}</span></span>
                          </div>
                          <div className="mt-2 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      )}

      {/* ESTADÍSTICAS */}
      {seccion === "estadisticas" && (
        <div className="space-y-5" data-testid="mkta-estadisticas">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-lg font-bold text-slate-900">Estadísticas del Marketplace</h2>
            <div className="flex items-center gap-2">
              <PeriodoSelector value={preset} onChange={setPreset} />
              <button onClick={exportarStats} disabled={bajandoCsv} data-testid="mkta-stats-csv" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-300 text-slate-700 text-sm font-bold hover:bg-slate-100 disabled:opacity-60">
                {bajandoCsv ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} CSV
              </button>
            </div>
          </div>
          {stats === undefined ? (
            <div className="flex items-center gap-2 text-slate-500 py-10"><Loader2 className="animate-spin" size={16} /> Cargando…</div>
          ) : !stats ? (
            <p className="text-sm text-slate-400 py-6">No se pudo cargar las estadísticas.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatKpi Icon={BarChart3} label="Volumen de ventas" value={solesFmt(stats.volumen_total)} color="#2563EB" />
                <StatKpi Icon={Wallet} label="Comisión generada" value={solesFmt(stats.comision_total)} color={MKT_GOLD} />
                <StatKpi Icon={CheckCircle2} label="Ventas" value={stats.ventas} color="#16A34A" />
                <StatKpi Icon={Wallet} label="Retiros pendientes" value={stats.retiros_pendientes} color="#CA8A04" />
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatKpi Icon={XCircle} label="Reembolsos" value={stats.reembolsos} color="#DC2626" />
                <StatKpi Icon={XCircle} label="Órdenes canceladas" value={stats.ordenes_canceladas} color="#64748B" />
              </div>
              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                <h3 className="font-bold text-slate-900 mb-3">Ventas por día</h3>
                <SerieBars serie={stats.serie} />
              </section>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <Ranking titulo="Ventas por plan" items={stats.por_plan} label={(it) => (it.plan || "").toUpperCase()} valor={(it) => `${it.ventas} · ${solesFmt(it.ingreso)}`} />
                <Ranking titulo="Ventas por categoría" items={stats.por_categoria} label={(it) => it.categoria} valor={(it) => solesFmt(it.ingreso)} />
                <Ranking titulo="Vendedores más activos" items={stats.top_vendedores} label={(it) => it.tienda} valor={(it) => `${it.ventas} · ${solesFmt(it.ingreso)}`} />
                <Ranking titulo="Productos/cursos más vendidos" items={stats.top_items} label={(it) => it.titulo} valor={(it) => `${it.ventas} vta(s)`} />
              </div>
            </>
          )}
        </div>
      )}

      {/* RETIROS */}
      {seccion === "retiros" && (
        <div className="space-y-4" data-testid="mkta-retiros">
          {retiros === undefined ? (
            <div className="flex items-center gap-2 text-slate-500 py-10"><Loader2 className="animate-spin" size={16} /> Cargando retiros…</div>
          ) : !retiros ? (
            <p className="text-sm text-slate-400 py-6">No se pudo cargar los retiros.</p>
          ) : retiros.items.length === 0 ? (
            <p className="text-sm text-slate-400 py-6">No hay solicitudes de retiro.</p>
          ) : (
            retiros.items.map((r) => {
              const estilo = { solicitado: "#CA8A04", aprobado: "#2563EB", pagado: "#16A34A", rechazado: "#DC2626" }[r.estado] || "#64748B";
              return (
                <div key={r.id} className={`bg-white rounded-2xl border shadow-soft p-5 transition-all ${highlightId === `mkta-retiro-${r.id}` ? "border-amber-400 ring-2 ring-amber-400 ring-offset-2" : "border-slate-100"}`} data-testid={`mkta-retiro-${r.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-bold text-slate-900 text-lg">S/ {Number(r.monto).toFixed(2)}</p>
                      <p className="text-sm text-slate-500">{r.tienda} · {r.vendedor}</p>
                      <p className="text-xs text-slate-400 mt-1">Método: {r.metodo} · Datos: {r.datos || "—"}</p>
                      {r.motivo_rechazo && <p className="text-xs text-red-600 mt-1">Rechazo: {r.motivo_rechazo}</p>}
                      {r.referencia_pago && <p className="text-xs text-emerald-600 mt-1">Ref. pago: {r.referencia_pago}</p>}
                    </div>
                    <span className="px-2.5 py-1 rounded-full text-xs font-bold uppercase" style={{ color: estilo, backgroundColor: `${estilo}15` }}>{r.estado}</span>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {r.estado === "solicitado" && (
                      <>
                        <button disabled={accionR === r.id} onClick={() => accionRetiro(apiAprobarRetiro, r.id)} data-testid={`retiro-aprobar-${r.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-sm font-bold bg-blue-600 hover:bg-blue-700 disabled:opacity-50"><CheckCircle2 size={15} /> Aprobar</button>
                        <button disabled={accionR === r.id} onClick={() => { const m = window.prompt("Motivo del rechazo:"); if (m) accionRetiro(apiRechazarRetiro, r.id, m); }} data-testid={`retiro-rechazar-${r.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50"><XCircle size={15} /> Rechazar</button>
                      </>
                    )}
                    {r.estado === "aprobado" && (
                      <button disabled={accionR === r.id} onClick={() => { const ref = window.prompt("Referencia del pago (opcional):") || ""; accionRetiro(apiPagarRetiro, r.id, ref); }} data-testid={`retiro-pagar-${r.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-sm font-bold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"><ArrowDownToLine size={15} /> Registrar pago</button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* REEMBOLSOS */}
      {seccion === "reembolsos" && (
        <div className="space-y-4" data-testid="mkta-reembolsos">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-4 text-sm text-slate-600">
            <p className="font-semibold text-slate-800 inline-flex items-center gap-2"><Info size={15} style={{ color: MKT_GOLD }} /> Regla de reembolsos</p>
            <p className="mt-1">Se permite solo dentro de los <b>3 días</b> de la compra y <b>si el comprador no ha descargado</b> el material. Al aprobar, se revoca el acceso y se ajusta el saldo del vendedor. La devolución del dinero se realiza en un máximo de 30 días.</p>
          </div>
          {reembolsos === undefined ? (
            <div className="flex items-center gap-2 text-slate-500 py-10"><Loader2 className="animate-spin" size={16} /> Cargando reembolsos…</div>
          ) : !reembolsos ? (
            <p className="text-sm text-slate-400 py-6">No se pudo cargar los reembolsos.</p>
          ) : reembolsos.items.length === 0 ? (
            <p className="text-sm text-slate-400 py-6">No hay solicitudes de reembolso.</p>
          ) : (
            reembolsos.items.map((r) => {
              const estilo = { solicitado: "#CA8A04", aprobado: "#16A34A", rechazado: "#DC2626" }[r.estado] || "#64748B";
              return (
                <div key={r.id} className={`bg-white rounded-2xl border shadow-soft p-5 transition-all ${highlightId === `mkta-reembolso-${r.id}` ? "border-amber-400 ring-2 ring-amber-400 ring-offset-2" : "border-slate-100"}`} data-testid={`mkta-reembolso-${r.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900">{r.titulo || "Recurso"}</p>
                      <p className="text-sm text-slate-500">{r.tipo === "curso" ? "Curso" : "Producto"} · {r.tienda || "—"} · S/ {Number(r.precio_pagado).toFixed(2)}</p>
                      <p className="text-xs text-slate-400 mt-1">Comprador: {r.comprador}</p>
                      <p className="text-sm text-slate-700 mt-2 bg-slate-50 rounded-lg px-3 py-2"><b>Motivo:</b> {r.motivo}</p>
                      {r.descargado ? (
                        <p className="text-xs font-semibold text-rose-600 mt-2 inline-flex items-center gap-1" data-testid={`reembolso-descargado-${r.id}`}><Download size={13} /> El comprador YA descargó el material · el reembolso no procede</p>
                      ) : (
                        <p className="text-xs font-semibold text-emerald-600 mt-2 inline-flex items-center gap-1" data-testid={`reembolso-no-descargado-${r.id}`}><CheckCircle2 size={13} /> El comprador no ha descargado el material</p>
                      )}
                      {r.comentario_admin && <p className="text-xs text-slate-500 mt-1">Comentario admin: {r.comentario_admin}</p>}
                    </div>
                    <span className="px-2.5 py-1 rounded-full text-xs font-bold uppercase" style={{ color: estilo, backgroundColor: `${estilo}15` }}>{r.estado}</span>
                  </div>
                  {r.estado === "solicitado" && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button disabled={accionRe === r.id || r.descargado} title={r.descargado ? "No se puede aprobar: material ya descargado" : ""} onClick={() => { const c = window.prompt("Comentario para el comprador (obligatorio):"); if (c && c.trim()) accionReembolso(apiAprobarReembolso, r.id, c.trim()); }} data-testid={`reembolso-aprobar-${r.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-sm font-bold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"><CheckCircle2 size={15} /> Aceptar reembolso</button>
                      <button disabled={accionRe === r.id} onClick={() => { const c = window.prompt("Motivo del rechazo (obligatorio):"); if (c && c.trim()) accionReembolso(apiRechazarReembolso, r.id, c.trim()); }} data-testid={`reembolso-rechazar-${r.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50"><XCircle size={15} /> Rechazar reembolso</button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* CONFIG */}
      {seccion === "config" && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6 max-w-2xl" data-testid="mkta-config">
          <h2 className="text-lg font-bold text-slate-900">Configuración de Marketplace</h2>
          <p className="text-sm text-slate-500 mt-1">Reglas comerciales y de moderación vigentes.</p>

          <div className="mt-5">
            <p className="text-sm font-bold text-slate-700 mb-2">Comisión de la plataforma por venta (según plan del vendedor)</p>
            <div className="grid grid-cols-3 gap-3" data-testid="mkta-config-comisiones">
              {(config?.comisiones || [
                { plan: "basico", label: "Básico", comision_pct: 15 },
                { plan: "pro", label: "Pro", comision_pct: 10 },
                { plan: "elite", label: "Elite", comision_pct: 8 },
              ]).map((c) => (
                <div key={c.plan} className="rounded-xl border border-slate-200 p-3 text-center" data-testid={`mkta-config-comision-${c.plan}`}>
                  <p className="text-xs font-semibold text-slate-500">{c.label}</p>
                  <p className="text-2xl font-extrabold" style={{ color: MKT_GOLD }}>{c.comision_pct}%</p>
                  <p className="text-[11px] text-slate-400">
                    {c.max_productos == null ? "Publicaciones ilimitadas" : `${c.max_productos} prod. / ${c.max_cursos} cursos`}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 space-y-4">
            {[
              { l: "Revisión obligatoria antes de publicar", v: "Activada" },
              { l: "Moneda predeterminada", v: `${config?.moneda || "PEN"} (S/)` },
              { l: "Insignia de vendedor verificado", v: "Manual (admin)" },
            ].map((r) => (
              <div key={r.l} className="flex items-center justify-between border-b border-slate-50 pb-3">
                <span className="text-sm text-slate-600">{r.l}</span>
                <span className="text-sm font-semibold text-slate-900">{r.v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {review && (
        <ReviewModal review={review} onClose={() => setReview(null)} onDecision={decidir} />
      )}
    </div>
  );
}

function ListaModeracion({ titulo, vacio, testid, children }) {
  const items = React.Children.toArray(children);
  return (
    <section className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6" data-testid={testid}>
      <h2 className="text-lg font-bold text-slate-900 mb-2">{titulo}</h2>
      {items.length === 0 ? <p className="text-sm text-slate-400 py-4">{vacio}</p> : <div>{children}</div>}
    </section>
  );
}

function ReviewModal({ review, onClose, onDecision }) {
  const { tipo, item } = review;
  const [comentario, setComentario] = useState("");
  const [verificado, setVerificado] = useState(false);
  const puedeDecidir = item.estado === "en_revision";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="mkta-review-modal">
      <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: MKT_GOLD }}>{tipo === "tienda" ? "Solicitud de tienda" : tipo === "curso" ? "Revisión de curso" : "Revisión de producto"}</span>
            <EstadoBadge estado={item.estado} tipo={tipo === "tienda" ? "tienda" : "producto"} />
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100" data-testid="mkta-review-cerrar"><X size={18} /></button>
        </div>

        <div className="p-5 max-h-[55vh] overflow-y-auto space-y-4">
          {tipo === "tienda" && <DetalleTienda t={item} />}
          {tipo === "producto" && <DetalleProducto p={item} />}
          {tipo === "curso" && <DetalleCurso c={item} />}

          {item.comentarios?.length > 0 && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3" data-testid="mkta-review-historial">
              <p className="text-xs font-bold text-slate-600 inline-flex items-center gap-1"><MessageSquareWarning size={13} /> Historial de comentarios</p>
              <ul className="mt-1.5 space-y-1.5">
                {item.comentarios.map((cm, i) => (
                  <li key={i} className="text-xs text-slate-600"><b>{cm.autor}</b> {cm.fecha ? `· ${cm.fecha}` : ""}: {cm.texto}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {puedeDecidir ? (
          <div className="p-5 border-t border-slate-100 space-y-3">
            <label className="block">
              <span className="text-sm font-semibold text-slate-700">Comentario administrativo <span style={{ color: MKT_GOLD }}>*</span></span>
              <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={2} placeholder="Escribe tu decisión o los cambios requeridos…" className="mt-1.5 w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm outline-none resize-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20" data-testid="mkta-review-comentario" />
            </label>
            {tipo === "tienda" && (
              <label className="flex items-center gap-2.5 text-sm text-slate-700">
                <input type="checkbox" checked={verificado} onChange={(e) => setVerificado(e.target.checked)} className="w-5 h-5 accent-emerald-600" data-testid="mkta-review-verificado" />
                <BadgeCheck size={16} className="text-emerald-600" /> Activar insignia "Vendedor verificado" al aprobar
              </label>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <button onClick={() => onDecision(tipo, item, "aprobado", comentario, verificado)} data-testid="mkta-review-aprobar" className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm">
                <CheckCircle2 size={16} /> Aprobar
              </button>
              <button onClick={() => onDecision(tipo, item, "correccion_solicitada", comentario, false)} data-testid="mkta-review-correccion" className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border font-bold text-sm" style={{ borderColor: MKT_GOLD, color: "#92400E" }}>
                <MessageSquareWarning size={16} /> Solicitar corrección
              </button>
              <button onClick={() => onDecision(tipo, item, "rechazado", comentario, false)} data-testid="mkta-review-rechazar" className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm">
                <XCircle size={16} /> Rechazar
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 border-t border-slate-100 text-sm text-slate-500">
            Este contenido ya fue moderado (estado actual: <b>{item.estado}</b>). Solo lectura.
          </div>
        )}
      </div>
    </div>
  );
}

function Dato({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-sm text-slate-800">{value || "—"}</p>
    </div>
  );
}

function DetalleTienda({ t }) {
  return (
    <div className="space-y-3" data-testid="mkta-detalle-tienda">
      <div className="rounded-xl overflow-hidden border border-slate-200">
        <div className="h-24" style={{ background: t.portada ? undefined : `linear-gradient(135deg, ${MKT_GOLD}, #B45309)` }}>{t.portada && <img src={t.portada} alt="" className="w-full h-full object-cover" />}</div>
        <div className="p-3 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl border-2 border-white -mt-8 bg-white shadow overflow-hidden flex items-center justify-center">{t.logo ? <img src={t.logo} alt="" className="w-full h-full object-cover" /> : <Store size={20} style={{ color: MKT_GOLD }} />}</div>
          <div><p className="font-bold text-slate-900">{t.nombre}</p><p className="text-xs text-slate-500">por {t.vendedor}</p></div>
        </div>
      </div>
      {t.descripcion && <p className="text-sm text-slate-600">{t.descripcion}</p>}
      <div className="grid grid-cols-2 gap-3">
        <Dato label="País / Ciudad" value={[t.ciudad, t.pais].filter(Boolean).join(", ")} />
        <Dato label="Teléfono" value={t.telefono} />
        <Dato label="Correo" value={t.correo} />
        <Dato label="Portafolio / Web" value={t.web} />
      </div>
      <Dato label="Categorías" value={(t.categorias || []).map(dcat).join(", ")} />
      <Dato label="Verificación" value={t.verificado ? "Vendedor verificado" : "No verificado"} />
    </div>
  );
}

function DetalleProducto({ p }) {
  return (
    <div className="space-y-3" data-testid="mkta-detalle-producto">
      <div className="rounded-xl overflow-hidden border border-slate-200 h-32 flex items-center justify-center" style={{ background: p.cover ? undefined : `linear-gradient(135deg, #FFFFFF, ${MKT_TINT})` }}>
        {p.cover ? <img src={p.cover} alt="" className="w-full h-full object-cover" /> : <Package size={30} style={{ color: MKT_GOLD }} />}
      </div>
      {p.galeria?.length > 0 && (
        <div className="flex gap-2">{p.galeria.map((g, i) => <img key={i} src={g} alt="" className="w-14 h-14 rounded-lg object-cover border border-slate-200" />)}</div>
      )}
      <div><p className="font-bold text-slate-900">{p.titulo}</p><p className="text-xs text-slate-500">{dcat(p.categoria)} · S/ {Number(p.precio || 0).toFixed(2)}</p></div>
      {p.descCompleta && <p className="text-sm text-slate-600 whitespace-pre-line">{p.descCompleta}</p>}
      <div className="grid grid-cols-2 gap-3">
        <Dato label="Versión" value={p.version} />
        <Dato label="Licencia" value={p.licencia} />
        <Dato label="Autoría declarada" value={p.autoria ? "Sí, confirmada" : "No"} />
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        {p.archivoUrl && (
          <a href={p.archivoUrl} target="_blank" rel="noopener noreferrer" data-testid="mkta-ver-archivo" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-sm font-bold hover:brightness-110" style={{ backgroundColor: MKT_GOLD }}>
            <Download size={15} /> Ver archivo{p.archivoNombre ? `: ${p.archivoNombre}` : ""}
          </a>
        )}
        {p.enlaceExterno && (
          <a href={p.enlaceExterno} target="_blank" rel="noopener noreferrer" data-testid="mkta-ver-enlace" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-bold hover:bg-slate-100">
            <Eye size={15} /> Ver enlace externo
          </a>
        )}
        {!p.archivoUrl && !p.enlaceExterno && <span className="text-xs text-slate-400">El vendedor no adjuntó archivo ni enlace.</span>}
      </div>
      <p className="text-[11px] text-slate-400 flex items-start gap-1"><Info size={12} className="mt-0.5" /> Producto creado por un vendedor independiente.</p>
    </div>
  );
}

function DetalleCurso({ c }) {
  return (
    <div className="space-y-3" data-testid="mkta-detalle-curso">
      <div className="rounded-xl overflow-hidden border border-slate-200 h-32 flex items-center justify-center" style={{ background: c.cover ? undefined : `linear-gradient(135deg, #FFFFFF, ${MKT_TINT})` }}>
        {c.cover ? <img src={c.cover} alt="" className="w-full h-full object-cover" /> : <GraduationCap size={30} style={{ color: MKT_GOLD }} />}
      </div>
      <div><p className="font-bold text-slate-900">{c.titulo}</p><p className="text-xs text-slate-500">{ccat(c.categoria)} · {c.instructor}</p></div>
      {c.galeria?.length > 0 && (
        <div className="flex gap-2">{c.galeria.map((g, i) => <img key={i} src={g} alt="" className="w-14 h-14 rounded-lg object-cover border border-slate-200" />)}</div>
      )}
      {c.descCompleta && <p className="text-sm text-slate-600 whitespace-pre-line">{c.descCompleta}</p>}
      <div className="flex items-center gap-4 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1"><Layers size={12} /> {nMod(c)} módulos</span>
        <span className="inline-flex items-center gap-1"><PlayCircle size={12} /> {nLec(c)} lecciones</span>
        <span className="font-bold text-slate-800">{Number(c.precio || 0) === 0 ? "Gratis" : `S/ ${Number(c.precio).toFixed(2)}`}</span>
      </div>
      {c.videoPresentacion && (
        <a href={c.videoPresentacion} target="_blank" rel="noopener noreferrer" data-testid="mkta-ver-presentacion" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-bold hover:bg-slate-100">
          <PlayCircle size={15} /> Ver video de presentación
        </a>
      )}
      {Array.isArray(c.modulos) && c.modulos.length > 0 && (
        <div className="rounded-lg border border-slate-200 p-3" data-testid="mkta-curso-temario">
          <p className="text-xs font-bold text-slate-600 mb-2">Temario y contenido</p>
          <div className="space-y-2.5">
            {c.modulos.map((m, mi) => (
              <div key={m.id || mi}>
                <p className="text-xs font-semibold text-slate-700">M{mi + 1}. {m.titulo}</p>
                <ul className="mt-1 space-y-1 pl-3">
                  {(m.lecciones || []).map((l, li) => (
                    <li key={l.id || li} className="flex items-center justify-between gap-2 text-xs">
                      <span className="text-slate-600 inline-flex items-center gap-1.5 min-w-0">
                        {l.tipo === "video" ? <PlayCircle size={12} style={{ color: MKT_GOLD }} /> : <Info size={12} className="text-slate-400" />}
                        <span className="truncate">{l.titulo || `Lección ${li + 1}`}</span>
                        <span className="text-slate-400">· {l.tipo || "contenido"}</span>
                      </span>
                      {l.recurso && (
                        <a href={l.recurso} target="_blank" rel="noopener noreferrer" data-testid={`mkta-ver-leccion-${mi}-${li}`} className="flex-shrink-0 font-bold hover:underline" style={{ color: MKT_GOLD }}>
                          {l.tipo === "video" ? "Ver video" : "Ver documento"}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
      <Dato label="Autoría declarada" value={c.autoria ? "Sí, confirmada" : "No"} />
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-[11px] text-amber-800">{COURSE_NOTICE}</div>
    </div>
  );
}
